import React from "react"
import {
  CheckboxGroup,
  RadioGroup,
  SectionCard,
  Field,
  MetricPill,
} from "./ClinicalHistoryAtoms"
import {
  inputStyle,
  textareaStyle,
  buttonStyle,
  primaryButtonStyle,
} from "./ClinicalHistoryStyles"

function ActionStrip({
  form,
  saving,
  isReadOnly,
  onPersistClinicalHistory,
  onSaveDraft,
  onOpenOdontogram,
  onOpenDiagnosis,
}) {
  return (
    <div
      style={{
        display: "grid",
        gap: 14,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,1fr) auto",
          gap: 14,
          alignItems: "start",
          padding: 14,
          borderRadius: 18,
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          border: "1px solid #e2e8f0",
          boxShadow: "0 10px 24px rgba(15,23,42,0.04)",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              color: "#1d4ed8",
              letterSpacing: 0.4,
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Resumen rápido
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <MetricPill
              label="Motivos"
              value={form.chiefComplaintTags.length || 0}
              tone="info"
            />
            <MetricPill label="Hábitos" value={form.habits.length || 0} />
            <MetricPill
              label="ATM"
              value={form.tmjIssues.length || 0}
              tone={form.tmjIssues.length ? "warning" : "neutral"}
            />
            <MetricPill
              label="Médicos"
              value={form.medicalConditions.length || 0}
              tone={form.medicalConditions.length ? "warning" : "neutral"}
            />
            <MetricPill
              label="Consentimiento"
              value={form.consentAccepted ? "Sí" : "No"}
              tone={form.consentAccepted ? "success" : "warning"}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onSaveDraft}
            style={{
              ...buttonStyle,
              background: "#f8fafc",
            }}
          >
            Guardar borrador
          </button>

          <button
            onClick={onOpenDiagnosis}
            disabled={isReadOnly}
            style={{
              ...buttonStyle,
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              color: "#1d4ed8",
              cursor: isReadOnly ? "not-allowed" : "pointer",
              opacity: isReadOnly ? 0.7 : 1,
            }}
          >
            Diagnóstico inicial
          </button>

          <button
            onClick={() => onOpenOdontogram(false)}
            style={buttonStyle}
          >
            Abrir odontograma
          </button>

          <button
            onClick={() => onPersistClinicalHistory()}
            disabled={saving || isReadOnly}
            style={{
              ...primaryButtonStyle,
              cursor: saving || isReadOnly ? "not-allowed" : "pointer",
              opacity: isReadOnly ? 0.7 : 1,
            }}
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  )
}

function SectionIntro({ text }) {
  return (
    <div
      style={{
        marginTop: 10,
        padding: "10px 12px",
        borderRadius: 12,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        fontSize: 12.5,
        color: "#64748b",
        lineHeight: 1.5,
      }}
    >
      {text}
    </div>
  )
}

export default function BaseClinicalHistoryForm({
  baseFormSectionCount,
  form,
  age,
  saving,
  isReadOnly,
  updateField,
  chiefComplaintOptions,
  habitOptions,
  tmjOptions,
  medicalOptions,
  onOpenOdontogram,
  onOpenDiagnosis,
  onPersistClinicalHistory,
  onSaveDraft,
}) {
  return (
    <>
      <SectionCard
        title="Formulario base general"
        subtitle="Captura clínica principal del paciente"
        badgeText={`${baseFormSectionCount} secciones`}
        badgeTone="warning"
        defaultOpen={true}
      >
        <ActionStrip
          form={form}
          saving={saving}
          isReadOnly={isReadOnly}
          onPersistClinicalHistory={onPersistClinicalHistory}
          onSaveDraft={onSaveDraft}
          onOpenOdontogram={onOpenOdontogram}
          onOpenDiagnosis={onOpenDiagnosis}
        />

        <div id="clinical-history-base-form" style={{ display: "grid", gap: 14 }}>
          <SectionCard
            title="1. Ficha de identificación"
            subtitle="Datos básicos del paciente"
            defaultOpen={true}
          >
            <SectionIntro text="Primero asegura los datos base del paciente. Este bloque debe quedar limpio porque alimenta la lectura del resto del expediente." />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                marginTop: 14,
              }}
            >
              <Field label="Fecha">
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateField("date", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Nombre(s)">
                <input
                  value={form.firstName}
                  onChange={(e) => updateField("firstName", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Apellido(s)">
                <input
                  value={form.lastName}
                  onChange={(e) => updateField("lastName", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Teléfono">
                <input
                  value={form.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Fecha de nacimiento">
                <input
                  type="date"
                  value={form.birthDate}
                  onChange={(e) => updateField("birthDate", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Edad">
                <input
                  value={age}
                  readOnly
                  style={{ ...inputStyle, background: "#f8fafc" }}
                />
              </Field>

              <Field label="Ocupación">
                <input
                  value={form.occupation}
                  onChange={(e) => updateField("occupation", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Escolaridad">
                <input
                  value={form.education}
                  onChange={(e) => updateField("education", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="Dirección">
                  <input
                    value={form.address}
                    onChange={(e) => updateField("address", e.target.value)}
                    style={inputStyle}
                    disabled={isReadOnly}
                  />
                </Field>
              </div>

              <Field label="Estado civil">
                <input
                  value={form.maritalStatus}
                  onChange={(e) => updateField("maritalStatus", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Género">
                <select
                  value={form.gender}
                  onChange={(e) => updateField("gender", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="FEMALE">Femenino</option>
                  <option value="MALE">Masculino</option>
                  <option value="OTHER">Otro</option>
                </select>
              </Field>
            </div>

            <div style={{ marginTop: 14 }}>
              <Field label="¿Alguien te recomendó?">
                <RadioGroup
                  name="recommended"
                  value={form.recommended}
                  onChange={(value) => updateField("recommended", value)}
                  options={[
                    { value: "YES", label: "Sí" },
                    { value: "NO", label: "No" },
                  ]}
                  disabled={isReadOnly}
                />
              </Field>
            </div>

            {form.recommended === "YES" ? (
              <div style={{ marginTop: 12 }}>
                <Field label="¿Quién te recomendó?">
                  <input
                    value={form.recommendedBy}
                    onChange={(e) => updateField("recommendedBy", e.target.value)}
                    style={inputStyle}
                    disabled={isReadOnly}
                  />
                </Field>
              </div>
            ) : null}
          </SectionCard>

          <SectionCard
            title="2. Motivo principal de consulta"
            subtitle="Qué trae al paciente hoy"
            defaultOpen={true}
          >
            <SectionIntro text="Aquí importa capturar rápido el problema que trae al paciente y el contexto del dolor o malestar actual." />

            <div style={{ marginTop: 14 }}>
              <Field label="Selecciona uno o varios motivos">
                <CheckboxGroup
                  options={chiefComplaintOptions}
                  values={form.chiefComplaintTags}
                  onChange={(value) => updateField("chiefComplaintTags", value)}
                  columns={3}
                  disabled={isReadOnly}
                />
              </Field>
            </div>

            {form.chiefComplaintTags.includes("OTHER") ? (
              <div style={{ marginTop: 12 }}>
                <Field label="Otro motivo">
                  <input
                    value={form.chiefComplaintOther}
                    onChange={(e) => updateField("chiefComplaintOther", e.target.value)}
                    style={inputStyle}
                    disabled={isReadOnly}
                  />
                </Field>
              </div>
            ) : null}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1.4fr) repeat(2, minmax(220px, 1fr))",
                gap: 12,
                marginTop: 12,
              }}
            >
              <Field label="Describe tu molestia">
                <textarea
                  value={form.chiefComplaintDescription}
                  onChange={(e) => updateField("chiefComplaintDescription", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Nivel de dolor (0-10)">
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={form.painLevel}
                  onChange={(e) => updateField("painLevel", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="¿Desde cuándo?">
                <input
                  value={form.symptomSince}
                  onChange={(e) => updateField("symptomSince", e.target.value)}
                  placeholder="Ej. 3 días"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="3. Historia odontológica previa"
            subtitle="Antecedentes dentales del paciente"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Última consulta odontológica">
                <input
                  type="date"
                  value={form.lastDentalVisitDate}
                  onChange={(e) => updateField("lastDentalVisitDate", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Motivo de la última consulta">
                <input
                  value={form.lastDentalVisitReason}
                  onChange={(e) => updateField("lastDentalVisitReason", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                marginTop: 12,
              }}
            >
              <Field label="¿Tratamiento ortodóntico previo?">
                <select
                  value={form.previousOrthodonticTreatment}
                  onChange={(e) => updateField("previousOrthodonticTreatment", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                </select>
              </Field>

              <Field label="¿Cirugías dentales previas?">
                <select
                  value={form.previousDentalSurgeries}
                  onChange={(e) => updateField("previousDentalSurgeries", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                </select>
              </Field>

              <Field label="¿Trauma dental previo?">
                <select
                  value={form.previousDentalTrauma}
                  onChange={(e) => updateField("previousDentalTrauma", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                </select>
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="4. Hábitos"
            subtitle="Importante para ortodoncia, pediatría y función"
            defaultOpen={false}
          >
            <Field label="Selecciona los hábitos presentes">
              <CheckboxGroup
                options={habitOptions}
                values={form.habits}
                onChange={(value) => updateField("habits", value)}
                columns={3}
                disabled={isReadOnly}
              />
            </Field>

            {!!form.habits.length ? (
              <div style={{ marginTop: 12 }}>
                <Field label="Duración o detalle del hábito">
                  <input
                    value={form.habitsDuration}
                    onChange={(e) => updateField("habitsDuration", e.target.value)}
                    placeholder="Ej. desde los 4 años / nocturno / frecuente"
                    style={inputStyle}
                    disabled={isReadOnly}
                  />
                </Field>
              </div>
            ) : null}
          </SectionCard>

          <SectionCard
            title="5. Higiene oral"
            subtitle="Rutina de higiene del paciente"
            defaultOpen={false}
          >
            <Field label="Frecuencia de cepillado">
              <RadioGroup
                name="brushingFrequency"
                value={form.brushingFrequency}
                onChange={(value) => updateField("brushingFrequency", value)}
                options={[
                  { value: "THREE", label: "3 veces al día" },
                  { value: "TWO", label: "2 veces al día" },
                  { value: "ONE", label: "1 vez al día" },
                  { value: "OCCASIONAL", label: "Ocasionalmente" },
                  { value: "NEVER", label: "Nunca" },
                ]}
                disabled={isReadOnly}
              />
            </Field>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                marginTop: 12,
              }}
            >
              <Field label="¿Usa hilo dental?">
                <select
                  value={form.flossUse}
                  onChange={(e) => updateField("flossUse", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                  <option value="SOMETIMES">A veces</option>
                </select>
              </Field>

              <Field label="¿Usa enjuague?">
                <select
                  value={form.mouthwashUse}
                  onChange={(e) => updateField("mouthwashUse", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                  <option value="SOMETIMES">A veces</option>
                </select>
              </Field>

              <Field label="¿Se hace limpiezas regularmente?">
                <select
                  value={form.regularCleaning}
                  onChange={(e) => updateField("regularCleaning", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                  <option value="SOMETIMES">A veces</option>
                </select>
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="6. ATM / problemas mandibulares"
            subtitle="Síntomas funcionales"
            defaultOpen={false}
          >
            <Field label="Selecciona los problemas presentes">
              <CheckboxGroup
                options={tmjOptions}
                values={form.tmjIssues}
                onChange={(value) => updateField("tmjIssues", value)}
                columns={2}
                disabled={isReadOnly}
              />
            </Field>

            {!!form.tmjIssues.length ? (
              <div style={{ marginTop: 12 }}>
                <Field label="Detalle">
                  <textarea
                    value={form.tmjDetail}
                    onChange={(e) => updateField("tmjDetail", e.target.value)}
                    style={textareaStyle}
                    disabled={isReadOnly}
                  />
                </Field>
              </div>
            ) : null}
          </SectionCard>

          <SectionCard
            title="7. Antecedentes médicos"
            subtitle="Información médica relevante"
            defaultOpen={false}
          >
            <Field label="Selecciona antecedentes relevantes">
              <CheckboxGroup
                options={medicalOptions}
                values={form.medicalConditions}
                onChange={(value) => updateField("medicalConditions", value)}
                columns={3}
                disabled={isReadOnly}
              />
            </Field>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                marginTop: 12,
              }}
            >
              <Field label="Medicamentos">
                <input
                  value={form.medications}
                  onChange={(e) => updateField("medications", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Detalle de alergias">
                <input
                  value={form.allergiesDetail}
                  onChange={(e) => updateField("allergiesDetail", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Notas médicas">
                <input
                  value={form.medicalNotes}
                  onChange={(e) => updateField("medicalNotes", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="8. Observaciones clínicas"
            subtitle="Notas generales del profesional"
            defaultOpen={false}
          >
            <Field label="Observaciones">
              <textarea
                value={form.observations}
                onChange={(e) => updateField("observations", e.target.value)}
                style={textareaStyle}
                disabled={isReadOnly}
              />
            </Field>
          </SectionCard>

          <SectionCard
            title="9. Odontograma y diagnóstico inicial"
            subtitle="Conexión entre historia clínica, hallazgos dentales y conducta sugerida"
            defaultOpen={false}
          >
            <SectionIntro text="Desde aquí conectas la historia clínica con la exploración dental y con el flujo de tratamiento." />

            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                marginTop: 14,
              }}
            >
              <button
                onClick={() => onOpenOdontogram(true)}
                disabled={saving || isReadOnly}
                style={{
                  ...primaryButtonStyle,
                  cursor: saving || isReadOnly ? "not-allowed" : "pointer",
                  opacity: isReadOnly ? 0.7 : 1,
                }}
              >
                Guardar y abrir odontograma
              </button>

              <button
                onClick={() => onOpenOdontogram(false)}
                style={buttonStyle}
              >
                Abrir sin guardar
              </button>

              <button
                onClick={onOpenDiagnosis}
                disabled={isReadOnly}
                style={{
                  ...buttonStyle,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1d4ed8",
                  cursor: isReadOnly ? "not-allowed" : "pointer",
                  opacity: isReadOnly ? 0.7 : 1,
                }}
              >
                Registrar diagnóstico inicial
              </button>
            </div>
          </SectionCard>

          <SectionCard
            title="10. Consentimiento"
            subtitle="Validación básica del registro"
            defaultOpen={true}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 14,
                color: "#334155",
                fontWeight: 600,
                cursor: isReadOnly ? "not-allowed" : "pointer",
                opacity: isReadOnly ? 0.75 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={form.consentAccepted}
                disabled={isReadOnly}
                onChange={(e) => updateField("consentAccepted", e.target.checked)}
              />
              Confirmo que la información fue proporcionada por el paciente y/o validada en consulta.
            </label>
          </SectionCard>
        </div>
      </SectionCard>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          flexWrap: "wrap",
          marginTop: 18,
        }}
      >
        <button
          onClick={onSaveDraft}
          style={{
            ...buttonStyle,
            background: "#f8fafc",
          }}
        >
          Guardar borrador
        </button>

        <button
          onClick={() => onPersistClinicalHistory()}
          disabled={saving || isReadOnly}
          style={{
            ...primaryButtonStyle,
            cursor: saving || isReadOnly ? "not-allowed" : "pointer",
            opacity: isReadOnly ? 0.7 : 1,
          }}
        >
          {saving ? "Guardando..." : "Guardar historia clínica"}
        </button>
      </div>
    </>
  )
}